import { useTranslation } from "react-i18next";
import styles from "./authBoot.module.css";

/**
 * What the shell does when the Keycloak handshake produced no session.
 *
 * `block` exists because the alternative — carrying on unauthenticated — is
 * indistinguishable from a permissions problem: with no token there is no role
 * and no site capability, so nav gating (ADR 120) hides every item except the
 * ungated `/alerts`, the site selector has no sites to offer, and the logout
 * button is conditional on the token. The result reads as "my account lost its
 * access", which is a lie: the login simply never happened. Say so instead.
 */
export type AuthBootOutcome = "authenticated" | "block" | "anonymous";

/**
 * @param requireAuth `VITE_REQUIRE_AUTH !== "false"` — security is on by default
 *                    (ADR 112). The `false` path is the deliberate no-auth dev
 *                    run (paired with `TWIN_SECURITY_DISABLED=true`), and it
 *                    keeps degrading silently: there, anonymous IS the intent.
 */
export function authBootOutcome(requireAuth: boolean, authed: boolean): AuthBootOutcome {
  if (authed) return "authenticated";
  return requireAuth ? "block" : "anonymous";
}

/** Blocking screen: names the server it tried, and why nothing is on screen. */
export function AuthBootError({ keycloakUrl, detail }: { keycloakUrl: string; detail?: string }) {
  const { t } = useTranslation();
  return (
    <div className={styles.screen} role="alert" data-testid="auth-boot-error">
      <div className={styles.card}>
        <h1 className={styles.title}>{t("auth.boot.title")}</h1>
        <p className={styles.lead}>{t("auth.boot.lead")}</p>
        <dl className={styles.facts}>
          <dt className={styles.factLabel}>{t("auth.boot.serverLabel")}</dt>
          <dd className={styles.factValue}>{keycloakUrl}</dd>
        </dl>
        {detail && <p className={styles.detail}>{detail}</p>}
        <p className={styles.hint}>{t("auth.boot.hint")}</p>
        <button type="button" className={styles.retry} onClick={() => window.location.reload()}>
          {t("auth.boot.retry")}
        </button>
      </div>
    </div>
  );
}
